/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * ============================================================================
 * TEMPORARY SHIM — DELETE THIS FILE WHEN THE PRODUCT FEED IS FIXED
 * ============================================================================
 *
 * WHAT IT DOES
 * Reorders a product's images so the `_j` asset precedes the `_c` asset, which
 * is the order live neimanmarcus.com / bergdorfgoodman.com serve.
 *
 * WHY IT EXISTS
 * Neither storefront selects images by shot type. Both take image[0] for the
 * product tile and image[1] for the hover swap. Live's arrays arrive `j, c, …`
 * so its tile shows the product alone and its hover shows the model. This
 * sandbox's arrays arrive `c, j, …`, so ours shows the model first and had
 * nothing sensible to hover to. Same trivial consumer logic on both sides,
 * opposite results, entirely because of the order the data arrives in.
 *
 * Verified against BM's Image Manager for BG cufflinks 0400001196535: the
 * Image Manager lists `_c` then `_j`, and SCAPI returns them in that same
 * order — so the ordering is what the catalog holds, not something SCAPI does.
 *
 * WHY IT IS A HACK AND NOT A RULE
 * The suffix does NOT encode "product vs model". It encodes a photography slot
 * whose meaning varies by category:
 *
 *   dresses (nm_5586297_100379):  _c = cropped MODEL shot,  _j = PRODUCT only
 *   cufflinks (bg_4914679_100244): _c = PRODUCT shot,       _j = PRODUCT shot,
 *                                  and the on-body shot is a third asset
 *                                  carrying neither suffix
 *
 * So this must never grow into "pick the `_j` because it is the product shot".
 * It is a positional correction that reproduces live's sequence, and it is
 * correct for the tile/hover pairing whatever the two shots actually depict.
 *
 * WHAT SHOULD REPLACE IT
 * Ordering belongs in the product data. Either the feed lands images in live's
 * order, or merchandising populates the `hi-res-model` view type — which SFCC
 * already models and which is the only semantically honest signal for "this is
 * the model shot". BM shows that view type existing but EMPTY on at least one
 * product, so today it cannot be relied on.
 *
 * HOW TO REMOVE IT
 * Delete this file and its call sites (import search: `applyLiveImageOrder`).
 * The tests in live-image-order.test.ts are deliberately written to fail loudly
 * rather than silently pass once ordering is fixed upstream, so the suite tells
 * you when this can go.
 */

/** Assets whose basename ends in this rank earlier, in this order. */
const LIVE_SUFFIX_ORDER = ['_j', '_c'] as const;

/** Extract the trailing two characters of an image's basename (`…_c` -> `_c`). */
function suffixOf(link: string | undefined): string | undefined {
    if (!link) return undefined;
    const basename = link.split('/').pop()?.split('?')[0];
    if (!basename) return undefined;
    const suffix = basename.slice(-2);
    return suffix.startsWith('_') ? suffix : undefined;
}

/**
 * Rank for sorting: known suffixes take their index, everything else sorts
 * after them while keeping its original relative position.
 */
function rankOf(link: string | undefined): number {
    const suffix = suffixOf(link);
    if (!suffix) return LIVE_SUFFIX_ORDER.length;
    const index = LIVE_SUFFIX_ORDER.indexOf(suffix as (typeof LIVE_SUFFIX_ORDER)[number]);
    return index === -1 ? LIVE_SUFFIX_ORDER.length : index;
}

/**
 * Reorder images into live's sequence.
 *
 * STABLE: assets that are not `_j` or `_c` keep their original order and stay
 * after the ranked ones, so a gallery's remaining shots are never shuffled.
 * Returns the SAME array reference when nothing needs to move, so callers can
 * rely on referential equality for memoisation.
 */
export function applyLiveImageOrder<T extends { link?: string }>(images: T[]): T[];
export function applyLiveImageOrder<T extends { link?: string }>(images: T[] | undefined): T[] | undefined;
export function applyLiveImageOrder<T extends { link?: string }>(images: T[] | undefined): T[] | undefined {
    if (!images || images.length < 2) return images;

    const ranked = images.map((image, index) => ({ image, index, rank: rankOf(image.link) }));
    const needsReorder = ranked.some((entry, i) => i > 0 && entry.rank < ranked[i - 1].rank);
    if (!needsReorder) return images;

    return ranked.sort((a, b) => a.rank - b.rank || a.index - b.index).map((entry) => entry.image);
}
